<template>
    <section>
        <div
            class="marketDirectoryModal"
            :class="{ 'padding-horizontal-100': !isMobile() }">
            <div class="padding-vertical-50">
                <svg
                    :class="`outlookClusterIcon outlookClusterIcon--${ activeOutlook.outlook }`"
                    viewBox="0 0 32 32"
                    preserveAspectRatio="xMidYMid meet"
                    width="10px"
                    height="10px"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true">
                    <path
                        class="outlookClusterIcon__iconPath"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
                <h1 class="sectionRooftitle">
                    {{ activeOutlook.name }}
                </h1>
                <h2 class="sectionHeadline margin-bottom-15">
                    {{ trans('OUTLOOK_MARKETOVERVIEW_TITLE') }}
                </h2>
            </div>
            <div class="flex flex--spacing30 flex--justifyContent-center flex--top">
                <template v-for="mainMarket in marketTreeData">
                    <div
                        :key="mainMarket.id"
                        class="flex__item marketDirectoryChild flex--directionColumn"
                        data-readingaid
                        data-readingaid-height="400">
                        <div class="panelCard">
                            <div
                                class="readingAid__text"
                                data-readingaid-text>
                                <div
                                    ref="directoryHeader"
                                    class="flex flex--center flex--nowrap padding-all-15 bg-light">
                                    <span :class="`flex__item deo-sprite market${ mainMarket.id }`"></span>
                                    <a
                                        class="flex__item flex__item--grow flex__item--basis text--linkGray"
                                        :data-gtm="`hubPageModal${ mainMarket.name }__idMarket--${ mainMarket.id }`"
                                        :href="mainMarket.slug">
                                        <div class="xmoMarketNameWrapper">
                                            <div
                                                :id="`market${ mainMarket.id }__marketDirectoryModal`"
                                                class="flex__item--grow flex__item--basis font-size-100 text-bold"
                                                :lang="locale">
                                                {{ mainMarket.name }}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                                <template v-if="mainMarket.children.length">
                                    <div
                                        v-for="child in mainMarket.children"
                                        :key="child.id">
                                        <div
                                            ref="directoryChild"
                                            class="pos-relative">
                                            <div :class="`padding-all-15 ${ child.children.length ? 'padding-right-50' : '' }  }} border-top-gray10-1`">
                                                <a
                                                    class="text--linkGray display-block"
                                                    :data-gtm="`hubPageModal${ child.name }__idMarket--${ child.id }`"
                                                    :href="child.slug">
                                                    <div class="xmoMarketNameWrapper">
                                                        <div
                                                            :id="`market${ child.id }__marketDirectoryModal`"
                                                            class="flex__item--grow flex__item--basis font-size-100"
                                                            :lang="locale">
                                                            {{ child.name }}
                                                        </div>
                                                    </div>
                                                </a>
                                                <template v-if="child.children.length && child.available === false">
                                                    <ul
                                                        v-for="grandchild in child.children"
                                                        id="listToggle"
                                                        :key="grandchild.id"
                                                        class="padding-left-10 font-size-s">
                                                        <li class="margin-top-10">
                                                            <a
                                                                class="text--linkLightGray"
                                                                :data-gtm="`hubPageModal${ grandchild.name }__idMarket--${ grandchild.id }`"
                                                                :href="grandchild.slug"
                                                                :lang="locale">
                                                                {{ grandchild.name }}
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </template>
                                            </div>
                                            <template v-if="child.children.length">
                                                <button
                                                    :id="`sm${ child.id }__marketDirectoryModal`"
                                                    type="button"
                                                    class="padding-vertical-0 button--reset marketDirectoryListToggle"
                                                    :data-gtm="`hubPageModal__${ child.name }--toggleChildMarkets`"
                                                    :aria-label="trans('GLOBAL_SHOW_MORE')"
                                                    :aria-labelledby="`sm${ child.id } market${ child.id }`"
                                                    @click="child.available = !child.available">
                                                    <svg
                                                        class="marketDirectoryListToggle__icon"
                                                        aria-hidden="true">
                                                        <use :href="child.available ? '#iconPlusBold' : '#iconMinusBold'" />
                                                    </svg>
                                                </button>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                                <div
                                    data-readingaid-gradient
                                    class="readingAid__gradient">
                                </div>
                            </div>
                            <button
                                :id="`sm${ mainMarket.id }__marketDirectoryModal`"
                                type="button"
                                class="marketDirectoryExpandButton readingAid__expand readingAid__expand--invisible"
                                :data-gtm="`hubPageModal__${ mainMarket.name }--showMore`"
                                :aria-labelledby="`sm${ mainMarket.id } market${ mainMarket.id }`"
                                :data-readingaid-toggle="trans('GLOBAL_SHOW_LESS')">
                                <span>{{ trans('GLOBAL_SHOW_MORE') }}</span>
                                <svg
                                    width="10"
                                    height="10"
                                    aria-hidden="true">
                                    <use href="#iconChevronDownReg" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </section>
</template>

<script>
    import Translator from 'tools/trans';
    import { initReadingAid } from 'tools/readingAid';
    import { isMobile } from 'tools/isMobile';

    const LOCALE = window.stGlobals.locale;
    const MAX_VISIBLE_CHILDREN = 6;

    export default {
        props: {
            marketData: { type: Object, required: true },
            activeOutlook: { type: Object, required: true },
            isOpen: { type: Boolean, required: true },
        },
        data() {
            return {
                locale: LOCALE,
            };
        },
        computed: {
            marketTreeData() {
                if (this.marketData?.market) {
                    return this.marketData.market.children;
                }

                return this.marketData.children;
            },
        },
        watch: {
            isOpen(newState) {
                if (newState === true) {
                    initReadingAid(this.heightFunction());
                }
            },
        },
        methods: {
            trans: Translator.trans,
            isMobile,
            heightFunction() {
                const header = this.$refs.directoryHeader;
                let children = this.$refs.directoryChild;
                let childrenHeight = 0;
                if (children) {
                    children = Array.prototype.slice.call(children).slice(0, MAX_VISIBLE_CHILDREN);
                    children.forEach(child => {
                        childrenHeight = childrenHeight + child.clientHeight;
                    });
                }
                const headerHeight = header.clientHeight;

                return headerHeight + childrenHeight;
            },
        },
    };
</script>
